import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiUrl } from '../lib/apiBase';

function readMeta(name: string): string {
    if (typeof document === 'undefined') return '';
    const el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    return el?.content?.trim() ?? '';
}

function getSlug(): string {
    // 1) Preferido: meta tag injetada pelo PwaClientShellController.
    const fromMeta = readMeta('tenant-slug');
    if (fromMeta) return fromMeta;

    // 2) Fallback: extrai do subdomínio do host (ex.: armazem.borabeber.app → armazem).
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const parts = host.split('.');
        // host como `slug.borabeber.app` (3) ou `slug.borabeber.local` (3).
        if (parts.length >= 3 && /^[a-z0-9][a-z0-9-]*$/i.test(parts[0])) {
            return parts[0];
        }
    }

    return '';
}

function tenantIdFromMeta(): string {
    return readMeta('tenant-id');
}

function brandingRemovedFromMeta(): boolean {
    return readMeta('branding-removed') === '1';
}

function fallbackTenantFromEnv(): string {
    const v = (import.meta.env.VITE_DEMO_TENANT_ID as string | undefined)?.trim();
    return v ?? '';
}

export const useAppMetaStore = defineStore('pwa-client-meta', () => {
    const slug = ref(getSlug());
    // tenantId fica disponível síncrona­mente quando a meta tag está presente
    // (caso comum em prod via PwaClientShellController). Em dev via Vite serve,
    // a meta não existe e o fetchMeta() preenche depois.
    const tenantId = ref(tenantIdFromMeta() || fallbackTenantFromEnv());
    const tradeName = ref('');
    const logoUrl = ref<string | null>(null);
    const primaryColor = ref('#FF6B00');
    const welcomeMessage = ref('');
    const closedMessage = ref('');
    const nextOpeningHint = ref('');
    const closingHint = ref('');
    const isOpen = ref<boolean | null>(null);
    const brandingRemoved = ref(brandingRemovedFromMeta());
    const whatsappEnabled = ref(true);
    const webChatEnabled = ref(false);
    const ready = ref(false);

    const initials = computed(() => {
        if (!tradeName.value) {
            return slug.value.slice(0, 2).toUpperCase() || 'ZG';
        }
        return tradeName.value
            .split(/\s+/u)
            .filter(Boolean)
            .slice(0, 2)
            .map(w => w[0].toUpperCase())
            .join('');
    });

    async function fetchMeta(): Promise<void> {
        const s = slug.value;
        // Mantém tenantId já resolvido via meta (não zera) — evita janela em que
        // chamadas de auth/API saem sem X-Tenant-ID.
        const preserveTenantId = tenantId.value;
        tradeName.value = '';
        logoUrl.value = null;
        welcomeMessage.value = '';
        closedMessage.value = '';
        nextOpeningHint.value = '';
        closingHint.value = '';

        try {
            if (s) {
                const res = await fetch(apiUrl(`public/tenant/${encodeURIComponent(s)}`), {
                    headers: { Accept: 'application/json' },
                });
                if (res.ok) {
                    const data = (await res.json()) as Record<string, unknown>;
                    tenantId.value = typeof data.id === 'string' ? data.id : preserveTenantId;
                    tradeName.value =
                        (typeof data.trade_name === 'string' && data.trade_name) ||
                        (typeof data.name === 'string' ? data.name : '');
                    logoUrl.value = typeof data.pwa_logo_url === 'string' ? data.pwa_logo_url : null;
                    primaryColor.value =
                        typeof data.pwa_primary_color === 'string' ? data.pwa_primary_color : '#FF6B00';
                    welcomeMessage.value =
                        typeof data.pwa_welcome_message === 'string' ? data.pwa_welcome_message : '';
                    closedMessage.value =
                        typeof data.pwa_closed_message === 'string' ? data.pwa_closed_message : '';
                    nextOpeningHint.value =
                        typeof data.next_opening_hint === 'string' ? data.next_opening_hint : '';
                    closingHint.value =
                        typeof data.closing_hint === 'string' ? data.closing_hint : '';
                    isOpen.value = typeof data.is_open === 'boolean' ? data.is_open : null;
                    brandingRemoved.value =
                        typeof data.pwa_remove_branding === 'boolean'
                            ? data.pwa_remove_branding
                            : brandingRemoved.value;
                    whatsappEnabled.value =
                        typeof data.whatsapp_enabled === 'boolean' ? data.whatsapp_enabled : whatsappEnabled.value;
                    webChatEnabled.value =
                        typeof data.web_chat_enabled === 'boolean' ? data.web_chat_enabled : webChatEnabled.value;
                }
            }
        } catch {
            // defaults
        } finally {
            if (!tenantId.value) {
                tenantId.value = preserveTenantId || fallbackTenantFromEnv();
            }
            ready.value = true;
        }
    }

    return {
        slug,
        tenantId,
        tradeName,
        logoUrl,
        primaryColor,
        welcomeMessage,
        closedMessage,
        nextOpeningHint,
        closingHint,
        isOpen,
        brandingRemoved,
        whatsappEnabled,
        webChatEnabled,
        ready,
        initials,
        fetchMeta,
    };
});
